import { createSelector } from "reselect"
import { get, chain, map, filter, orderBy, find, first, isNumber, size } from "lodash"
import pluralize from "pluralize"
import { getGrouping } from "helpers/compositeSecurities"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups, getRollupSummaryLabel } from "helpers/rollups"
import { getScreenName } from "modules/analyze-composite-forecast/selectors/file"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { computeSelectedGroup } from "helpers/analyze"
import { VIEW_TYPES } from "helpers/base"
import { createQueryStringSelector } from "selectors/queryString"
import { makeGroupSort } from "helpers/contributorsSort"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

export const getSelectedContributorCategory = (_, props) => props.selectedContributorCategory
const getPositionSetName = (_, props) => props.positionSetName
const getSelectedGroupId = createQueryStringSelector("contributorGroup")
const getContributorsGrouping = (_, props) => props.contributorsGrouping
const getSelectedDate = (_, props) => props.selectedDate
const getOrder = (_, props) => get(props, "sortOrder", EMPTY_OBJECT)
const getClassifications = (_, props) => props.classifications
const getContributorsData = state => get(state, "global.data.compositeForecastContributors.data.model.simulation.forecastContributors.contributors")
const getGroupedContributorsData = state => get(state, "global.data.compositeGroupedForecastContributors.data.model.simulation.groupedForecastContributors.groupedContributors")
const getRawGroupingId = createQueryStringSelector("grouping")
const getRawGroupingType = createQueryStringSelector("groupingType")

const getSelectedField = createSelector(
  [getSelectedContributorCategory],
  (selected) => get(selected, "field", "total"),
)

const getContributors = createSelector(
  [getContributorsData],
  (data) => {
    return map(data, security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)

      return {
        id: security.id,
        country: security.country,
        description: security.description,
        classification: get(security, "classification.name"),
        currency: security.currency,
        assetClass: security.assetClass,
        size: security.percentEquity,
        bloombergTicker: security.bloombergTicker,
        modelProviderId: security.modelProviderId,
        factors: {
          expectedReturn: security.weightedExpectedReturn / 100,
        },
        ticker,
        exchange,
      }
    })
  },
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) => {
    return filter(data, security => {
      return get(security, selectedField) != null
    })
  },
)

export const getSelectedGroup = createSelector(
  [getSelectedGroupId, getGroupedContributorsData],
  (selectedGroupId, contributors) => computeSelectedGroup({ id: selectedGroupId }, contributors),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return orderBy(data, [
      (security) => {
        switch (order.by) {
          case "name":
            return security.id
          case "equity":
            return order.abs ? Math.abs(security.size) : security.size
          default: {
            const value = get(security, selectedField)
            return order.abs && isNumber(value) ? Math.abs(value) : value
          }
        }
      },
    ], [order.dir])
  },
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData],
  (data) => map(data, group => ({
    ...group,
    factors: {
      expectedReturn: group.totalExpectedReturn / 100,
    },
    contributors: map(group.contributors, security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
      return {
        id: security.id,
        country: security.country,
        description: security.description,
        classification: get(security, "classification.name"),
        currency: security.currency,
        assetClass: security.assetClass,
        size: security.percentEquity,
        bloombergTicker: security.bloombergTicker,
        modelProviderId: security.modelProviderId,
        factors: {
          expectedReturn: security.weightedExpectedReturn / 100,
        },
        ticker,
        exchange,
      }
    }),
  })),
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  (data, selectedField, selectedGroup) => {
    return chain(data)
      .filter(group => !selectedGroup || selectedGroup.id === group.id)
      .map(group => ({
        ...group,
        contributors: filter(group.contributors, security => get(security, selectedField)),
      }))
      .value()
  },
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getOrder],
  (data, selectedField, order) => {
    if (!size(data)) return EMPTY_ARRAY

    return chain(data)
      .map(group => ({
        ...group,
        value: get(group, selectedField),
        contributors: orderBy(group.contributors, [
          (security) => {
            switch (order.by) {
              case "name":
                return security.id
              case "equity":
                return order.abs ? Math.abs(security.size) : security.size
              default: {
                const value = get(security, selectedField)
                return order.abs && isNumber(value) ? Math.abs(value) : value
              }
            }
          },
        ], [order.dir]),
      }))
      .orderBy(...makeGroupSort(order))
      .value()
  },
)

const getAccountRollups = createSelector(
  [getClassifications],
  (classifications) => ({
    classifications,
  }),
)
export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate],
  getAllAvailableRollups,
)

export const getCurrentGrouping = createSelector(
  [getRawGroupingId, getRawGroupingType, getAvailableRollups],
  getGrouping,
)

export const getContributorsSummaryText = createSelector(
  [getContributorsGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup],
  (contributorsGrouping, contributors, groups, selectedGroup) => {
    if (selectedGroup) {
      const numberOfSecurities = get(first(groups), "contributors.length", 0)
      return `${numberOfSecurities} ${pluralize("Security", numberOfSecurities)}`
    } else if (contributorsGrouping) {
      return `${groups.length} ${pluralize(getRollupSummaryLabel(contributorsGrouping), groups.length)}`
    } else {
      return `${contributors.length} ${pluralize("Security", contributors.length)}`
    }
  },
)

export const getExcelData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelectedContributorCategory, getContributorsGrouping, getPositionSetName, getSelectedDate, getSelectedGroup, getScreenName],
  (data, groupedData, selectedContributorCategory, contributorsGrouping, positionSetName, selectedDate, selectedGroup, screenName) => {
    if (selectedGroup) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        type: screenName,
        portfolioName: positionSetName,
        selected: selectedContributorCategory,
        selectedDate,
        viewType: VIEW_TYPES.ABSOLUTE,
        group,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        type: screenName,
        portfolioName: positionSetName,
        selected: selectedContributorCategory,
        selectedDate,
        viewType: VIEW_TYPES.ABSOLUTE,
        contributorsGrouping,
        data: groupedData,
      })
    }

    return computeContributorsCSV({
      type: screenName,
      portfolioName: positionSetName,
      selected: selectedContributorCategory,
      selectedDate,
      viewType: VIEW_TYPES.ABSOLUTE,
      data,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      const group = find(groupedContributors, { id: selectedGroup.id })
      return get(group, selectedField) * 100
    }

    return null
  },
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      return chain(groupedContributors)
        .map(group => ({
          ...group,
          value: get(group, selectedField),
        }))
        .orderBy(["name"], ["asc"])
        .value()
    }

    return null
  },
)
